import { createContext, useContext, useState, useEffect } from "react";
import { authenticatedApi, publicApi } from "../lib/api";

const SectionsContext = createContext();

export function SectionsProvider({ children }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get event slug from URL
  const getEventSlug = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    return pathParts[1]; // /e/:eventSlug format
  };

  // Check if user is authenticated for current event
  const isAuthenticated = () => {
    const eventSlug = getEventSlug();
    if (!eventSlug) return false;

    const sessionKey = `attendee_session_${eventSlug}`;
    const session = localStorage.getItem(sessionKey);
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return !!sessionData.session_token;
      } catch (error) {
        localStorage.removeItem(sessionKey);
        return false;
      }
    }
    return false;
  };

  // Load sections data
  const loadSections = async () => {
    const eventSlug = getEventSlug();
    if (!eventSlug) return;

    try {
      setLoading(true);
      setError(null);

      const isAuth = isAuthenticated();

      // Use authenticated API if user is logged in, otherwise use public API
      const api = isAuth ? authenticatedApi : publicApi;

      const sectionsData = await api.getSections(eventSlug);

      setSections(sectionsData);
    } catch (err) {
      setError(err.message || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  // Load sections on mount and when authentication state changes
  useEffect(() => {
    loadSections();
  }, []);

  // Reload sections when authentication state changes
  useEffect(() => {
    const handleStorageChange = () => {
      loadSections();
    };

    const handleSessionUpdate = () => {
      loadSections();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("attendee_session_updated", handleSessionUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "attendee_session_updated",
        handleSessionUpdate
      );
    };
  }, []);

  const value = {
    sections,
    loading,
    error,
    reloadSections: loadSections,
  };

  return (
    <SectionsContext.Provider value={value}>
      {children}
    </SectionsContext.Provider>
  );
}

export function useSections() {
  const context = useContext(SectionsContext);
  if (context === undefined) {
    throw new Error("useSections must be used within a SectionsProvider");
  }
  return context;
}
